import type { ReactNode, RefObject } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useInvoicingOverview } from '../hooks/useInvoicingOverview';
import { useReverseInvoiceDialog } from '../hooks/useReverseInvoiceDialog';
import { ReverseInvoiceModal } from './ReverseInvoiceModal/ReverseInvoiceModal';
import { InvoicingSummaryTiles } from './InvoicingSummaryTiles';
import { StillToInvoiceSection } from './StillToInvoiceSection';
import { SubmittedInvoicesSection } from './SubmittedInvoicesSection';
import type { InvoiceReversal } from '../hooks/useInvoiceReversal';
import type { AgreementInvoicesQuery, SoldItemsQuery } from '../types/agreements.types';

interface Props {
    invoicesQuery: AgreementInvoicesQuery;
    soldItemsQuery: SoldItemsQuery;
    availableBalance: string | null;
    balanceUnreadableReason: string | null;
    dealerNames: Map<string, string>;
    headingRef: RefObject<HTMLHeadingElement | null>;
    action: ReactNode;
    notice: ReactNode;
    reversal?: InvoiceReversal;
}

export function PreviouslyInvoicedScreen({
    invoicesQuery,
    soldItemsQuery,
    availableBalance,
    balanceUnreadableReason,
    dealerNames,
    headingRef,
    action,
    notice,
    reversal,
}: Props) {
    const overview = useInvoicingOverview({ invoicesQuery, soldItemsQuery, availableBalance, balanceUnreadableReason });
    const dialog = useReverseInvoiceDialog(reversal);

    return (
        <PermissionGuard permission={{ resource: 'invoice', action: 'browse' }}>
            <div className="@container space-y-4">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-semibold text-mp-foreground">Invoicing</h3>
                    {action}
                </div>

                {notice}

                <InvoicingSummaryTiles tiles={overview.tiles} />

                <div className="grid gap-6 @min-[58rem]:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] @min-[58rem]:items-start">
                    <SubmittedInvoicesSection
                        invoices={invoicesQuery.data}
                        isLoading={invoicesQuery.isLoading}
                        isError={invoicesQuery.isError}
                        refetch={invoicesQuery.refetch}
                        dealerNames={dealerNames}
                        headingRef={headingRef}
                        canReverse={reversal?.canReverse}
                        onReverse={reversal ? dialog.open : undefined}
                    />
                    <StillToInvoiceSection {...overview.stillToInvoice} />
                </div>
            </div>
            {reversal && dialog.target && (
                <ReverseInvoiceModal
                    isOpen
                    invoice={dialog.target}
                    pending={reversal.pending}
                    serverError={reversal.errorMessage}
                    onSubmit={dialog.submit}
                    onClose={dialog.close}
                />
            )}
        </PermissionGuard>
    );
}
